import { computed, ref, shallowRef, watch } from "vue";
import { toBoolean, toJSON } from "#ce/utils/props";
import { firstDetail } from "#ce/utils/events";
import { useForm } from "#ce/composables/useForm";
import { ANY_OPTION, idOfRowValue } from "#ce/shared/filters/category/row-values";

/**
 * The selection model for a `CategoryFilter`: the ids currently selected, kept in sync with
 * `props.modelValue` and `props.single`, and mirrored to the host's form value under `props.name`.
 *
 * @param {Object} props - The component's `props` (`modelValue`, `single`, `nonClearable`, `name`,
 *   `anyLabel`).
 * @param {Function} emit - The component's `emit`, used for `update:model-value`.
 * @returns {Object}
 *   @returns {import("vue").Ref<Array<String>>} selectedIds - The ids currently selected.
 *   @returns {import("vue").ShallowRef<Array<String>>} selectedRowValues - The row values the
 *     picker marks: the selected ids, or `ANY_OPTION` while the clearing row stands in for none.
 *   @returns {import("vue").ComputedRef<Boolean>} offersAny - Whether the clearing row is offered.
 *   @returns {import("vue").ComputedRef<Boolean>} isMultiple - Whether more than one id may be held.
 *   @returns {import("vue").ComputedRef<Boolean>} hasSelection - Whether any id is selected.
 *   @returns {Function} handleUpdateSelected - `sp-quantum-select`'s `update-selected` listener.
 *   @returns {Function} clear - Clears the selection and reports the change, when clearing is
 *     allowed.
 */
export function useCategoryFilterSelection(props, emit) {
  const isMultiple = computed(() => !toBoolean(props.single));

  // Ids are held as strings, the form the URL param carries and the row values compare in. A
  // string is a JSON array of ids, or one bare id: `'["a","b"]'` → `["a", "b"]`, `"106"` → `["106"]`.
  function toIds(value) {
    let ids = value;

    if (typeof value === "string") {
      const text = value.trim();

      if (text.startsWith("[")) {
        ids = toJSON(text, true);
      } else {
        ids = text ? [text] : [];
      }
    }

    return (Array.isArray(ids) ? ids : []).map(String).filter(Boolean);
  }

  // Returns the ids the selection holds: all of them, or the first alone under `single`.
  function parseIds(value) {
    const ids = toIds(value);

    return isMultiple.value ? ids : ids.slice(0, 1);
  }

  // Reports a selection the filter narrowed on its own, so the host's model matches what the filter
  // holds. `seeded` tells the host to move its model without dispatching a change.
  function reportNarrowedSelection() {
    emit("update:model-value", selectedIds.value.length ? [...selectedIds.value] : undefined, { seeded: true });
  }

  function areIdsEqual(a, b) {
    return a.length === b.length && a.every((id, index) => id === b[index]);
  }

  const selectedIds = ref(parseIds(props.modelValue));

  // A prop sync mirrors silently; only a user's pick or clear reports a change upward. A seed
  // carrying more ids than `single` holds is the exception: the narrowing is reported, so
  // `host.value` reads what the filter holds.
  function syncSelectedIdsFromModelValue(value) {
    const next = parseIds(value);

    if (!areIdsEqual(next, selectedIds.value)) {
      selectedIds.value = next;
    }

    if (next.length < toIds(value).length) {
      reportNarrowedSelection();
    }
  }

  // `single` caps how many ids the selection may hold, so tightening it trims the live selection.
  // `modelValue` can lag a pick the parent has not mirrored back yet, so it is not re-read here.
  function trimSelectionToSingle() {
    if (isMultiple.value || selectedIds.value.length <= 1) {
      return;
    }

    selectedIds.value = selectedIds.value.slice(0, 1);
    reportNarrowedSelection();
  }

  watch(() => props.modelValue, syncSelectedIdsFromModelValue, { immediate: true });
  watch(() => props.single, trimSelectionToSingle);

  const hasSelection = computed(() => selectedIds.value.length > 0);

  // A cleared filter reports `undefined`, which the host reads as inactive.
  function emitModelValue() {
    emit("update:model-value", hasSelection.value ? [...selectedIds.value] : undefined);
  }

  // The host dispatches the one change per user update; each selected id is submitted as its own
  // entry under `name`, the way a native <select multiple> submits.
  const { mirrorFormValue } = useForm();

  mirrorFormValue(
    computed(() => {
      const data = new FormData();
      selectedIds.value.forEach((id) => data.append(props.name, id));

      return data;
    }),
  );

  // The clearing row is offered only where clearing is allowed.
  const offersAny = computed(() => Boolean(props.anyLabel) && !toBoolean(props.nonClearable));

  // The row values `sp-quantum-select` marks, with the clearing row standing in for no selection.
  function toSelectedRowValues() {
    return offersAny.value && !hasSelection.value ? [ANY_OPTION] : [...selectedIds.value];
  }

  // A fresh array on every write, so the picker re-marks its rows even when the values are equal
  // to the ones it was last given.
  const selectedRowValues = shallowRef(toSelectedRowValues());

  function syncSelectedRowValues() {
    selectedRowValues.value = toSelectedRowValues();
  }

  watch([selectedIds, offersAny], syncSelectedRowValues);

  // The clearing row picked over a selection clears it. Alongside no selection it is the mark the
  // row already carried, and is dropped.
  function handleUpdateSelected(event) {
    // A repeated id's rows carry distinct values, which all stand for the one id.
    const picked = [...new Set(toIds(firstDetail(event) ?? []).map(idOfRowValue))];
    const clears = picked.includes(ANY_OPTION) && hasSelection.value;
    const next = clears ? [] : parseIds(picked.filter((id) => id !== ANY_OPTION));

    if (areIdsEqual(next, selectedIds.value)) {
      syncSelectedRowValues();

      return;
    }

    selectedIds.value = next;
    emitModelValue();
  }

  function clear() {
    if (!hasSelection.value || toBoolean(props.nonClearable)) {
      return;
    }

    selectedIds.value = [];
    emitModelValue();
  }

  return { selectedIds, selectedRowValues, offersAny, isMultiple, hasSelection, handleUpdateSelected, clear };
}
